/**
 * Hold-Space push-to-talk — pure contract shared by the desktop producer
 * (`desktop/HoldToTalkControl.tsx`, dispatches) and the shared consumer
 * (`components/InputBar.tsx`, listens) — #1657 Phase 5b.
 *
 * ARCHITECTURE (plan §2.6 PTTButton row + §3.2 "recorder hooks shared"):
 * the Space key is a VIEW-LAYER TRIGGER only. Recording start/stop stays owned
 * by InputBar's existing mic paths (`useRecorderDock` whisper dock / Web Speech
 * toggle); the trigger travels as transient `document` CustomEvents mirroring
 * the blessed `focus-composer` / `auto-submit` precedent (R-A2 bucket 4 — a
 * transient view trigger, NOT a state synthesis; no store bypass). Recording
 * STATE keeps living where it lives today (`composerStore.globalRecording` +
 * the dock) — the desktop indicator renders FROM the store, never from events.
 *
 * Everything here is a pure function so the edge rules (repeat-guard, typing
 * gate, click-vs-hold ownership, quick-tap race) are unit-testable in node
 * (see holdToTalk.spec.ts — the in-env behavioral half of SE-DS-08).
 */

/** Dispatched on `document` (NOT window — the PlanChip gotcha: InputBar listens
 *  on document and DOM events don't propagate down) when a Space hold begins. */
export const PTT_HOLD_START = 'ptt-hold-start';
/** Dispatched on `document` when the Space hold ends (keyup / blur / unmount). */
export const PTT_HOLD_END = 'ptt-hold-end';

/** Mic init (`getUserMedia`) is async — a quick Space tap can release BEFORE
 *  `dock.isRecording` flips true. The consumer remembers the release for this
 *  window and stops the instant recording engages, so a tap can never leave the
 *  mic silently hot. Kept short: a same-gesture race cover, not a permission-
 *  prompt cover (a >3s first-time permission grant keeps the recording, with
 *  the indicator visible — the user can stop by click or another Space tap). */
export const PENDING_HOLD_STOP_WINDOW_MS = 3000;

export interface HoldKeyLike {
  code: string;
  repeat: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/** True when a keydown is a fresh, unmodified Space press — the ONLY producer
 *  engage trigger. `repeat` is excluded so OS key-repeat never retriggers;
 *  every modifier is excluded so browser/OS chords (Shift+Space scroll-up,
 *  Ctrl+Space IME switchers) are never stolen. */
export function isSpaceHoldTrigger(e: HoldKeyLike): boolean {
  return (
    e.code === 'Space' &&
    !e.repeat &&
    !e.metaKey &&
    !e.ctrlKey &&
    !e.altKey &&
    !e.shiftKey
  );
}

/** Elements Space must NEVER be stolen from: text entry (types a space, as
 *  normal) and interactive controls (Space activates buttons/links/selects —
 *  WCAG 2.1.1 keyboard operation). Structurally typed so it's testable with
 *  plain objects in node (no DOM classes needed). */
export function isTextEntryOrInteractive(
  el: { tagName?: string; isContentEditable?: boolean } | null | undefined,
): boolean {
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    tag === 'BUTTON' ||
    tag === 'A'
  ) {
    return true;
  }
  return !!el.isContentEditable;
}

/** Consumer-side decision for `ptt-hold-start`. `toggle: true` means "call the
 *  SAME handler the rendered mic control uses" (which starts, since idle).
 *
 *  `holdInitiated` may only become true when the toggle will ACTUALLY attempt
 *  a start — it is gated on the same preconditions `handleMicToggle` checks
 *  (`busy`, `micHeldElsewhere`). Otherwise a hold that no-ops would still arm
 *  the keyup's pendingStop window, and a recording started by CLICK within
 *  that window would be instantly killed by the stale flag (repair of PR
 *  #1800 review finding 1 — violates the locked "a stray Space keyup must NOT
 *  stop a click-started recording" rule via the pending window).
 *
 *  - recording in flight (click-started, or another session's dot) → NEVER
 *    adopted: no toggle, no ownership (locked edge rule);
 *  - busy (assistant turn in flight) → handleMicToggle would `return` before
 *    starting anything: no toggle, no ownership;
 *  - mic held elsewhere → toggle: true so the shared handler surfaces its
 *    "recording in another session" hint (feedback for the hold gesture), but
 *    holdInitiated stays false — the hold owns nothing;
 *  - idle → toggle + own. */
export function decideHoldStart(
  isRecording: boolean,
  busy: boolean,
  micHeldElsewhere: boolean,
): { toggle: boolean; holdInitiated: boolean } {
  if (isRecording) return { toggle: false, holdInitiated: false };
  if (busy) return { toggle: false, holdInitiated: false };
  if (micHeldElsewhere) return { toggle: true, holdInitiated: false };
  return { toggle: true, holdInitiated: true };
}

/** Consumer-side decision for `ptt-hold-end`.
 *  - not hold-initiated → no-op (stray keyup, or a click-started recording);
 *  - hold-initiated + recording → toggle (stops → normal transcription);
 *  - hold-initiated + NOT yet recording → the quick-tap race: mic init is
 *    still pending, so arm `pendingStop` (see PENDING_HOLD_STOP_WINDOW_MS).
 *
 *  COMPANION CONTRACT (enforced in InputBar, not expressible here): when a
 *  hold-initiated start attempt dies WITHOUT ever recording — mic permission
 *  denied (dock.start's async error callback), Web Speech unsupported/blocked,
 *  or the recording is stopped out-of-band (mic click mid-hold) — the consumer
 *  must release hold ownership (`holdInitiated = false`, pending window
 *  cleared) so a stale pendingStop can never kill a later click-started
 *  recording. */
export function decideHoldEnd(
  holdInitiated: boolean,
  isRecording: boolean,
): { toggle: boolean; holdInitiated: boolean; pendingStop: boolean } {
  if (!holdInitiated) return { toggle: false, holdInitiated: false, pendingStop: false };
  if (isRecording) return { toggle: true, holdInitiated: false, pendingStop: false };
  return { toggle: false, holdInitiated: false, pendingStop: true };
}
